/**
 * useFontScale — persists a per-device UI scale preference and applies it to
 * the document's root font-size. Reads from localStorage on mount so the
 * setting sticks across sessions. Called once at app root by
 * `<FontScaleApplier />`.
 *
 * Scales are named + concrete-pixel because the app's global scale (Iter 82)
 * dropped the root to 12px; the "Small" option matches that shrunk baseline.
 */
import { useEffect, useState, useCallback } from 'react';

export const FONT_SCALE_KEY = 'shipitnow.fontScale';

export const FONT_SCALES = [
  { value: 'small', label: 'Small', px: 12 },
  { value: 'default', label: 'Default', px: 13 },
  { value: 'medium', label: 'Larger', px: 15 },
  { value: 'large', label: 'Largest', px: 17 },
];

const applyToRoot = (px) => {
  if (typeof document !== 'undefined') {
    document.documentElement.style.fontSize = `${px}px`;
  }
};

export const readSavedScale = () => {
  try {
    const raw = localStorage.getItem(FONT_SCALE_KEY);
    if (!raw) return 'small';
    return FONT_SCALES.some((s) => s.value === raw) ? raw : 'small';
  } catch { return 'small'; }
};

export function useFontScale() {
  const [scale, setScaleState] = useState(readSavedScale);

  useEffect(() => {
    const found = FONT_SCALES.find((s) => s.value === scale) || FONT_SCALES[0];
    applyToRoot(found.px);
    try { localStorage.setItem(FONT_SCALE_KEY, scale); } catch { /* private / quota */ }
  }, [scale]);

  const setScale = useCallback((next) => {
    setScaleState(next);
  }, []);

  return { scale, setScale, options: FONT_SCALES };
}
